import React, { useState, useRef, useEffect } from 'react';


import Button from '../../../components/ui/Button';
import { renderAnnotationsOnCanvas } from '../../../services/annotationService';

const AnnotatedImage = ({ image, annotations, showAnnotations }) => {
  const [isZoomed, setIsZoomed] = useState(false);
  const [scale, setScale] = useState(1);
  const [imageLoaded, setImageLoaded] = useState(false);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const imgRef = useRef(null);

  // Reset imageLoaded whenever the image source changes so that the canvas
  // waits for the new image to fully load before computing dimensions.
  useEffect(() => {
    setImageLoaded(false);
  }, [image?.url]);

  useEffect(() => {
    if (showAnnotations && imageLoaded && canvasRef?.current && imgRef?.current) {
      const canvas = canvasRef?.current;
      const img = imgRef?.current;
      
      canvas.width = img?.naturalWidth || img?.width;
      canvas.height = img?.naturalHeight || img?.height;
      
      renderAnnotationsOnCanvas(canvas, annotations, canvas?.width, canvas?.height);
    }
  }, [showAnnotations, annotations, imageLoaded]);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleZoomToggle = () => {
    setIsZoomed(!isZoomed);
    setScale(isZoomed ? 1 : 2);
  };

  return (
    <div className="relative bg-muted rounded-lg overflow-hidden">
      <div 
        ref={containerRef}
        className="relative w-full"
        style={{ 
          height: '400px',
          transform: `scale(${scale})`,
          transformOrigin: 'center',
          transition: 'transform 0.3s ease'
        }}
      >
        <img
          ref={imgRef}
          src={image?.url}
          alt={image?.alt || 'Fire scene evidence'}
          className="w-full h-full object-contain"
          onLoad={handleImageLoad}
          crossOrigin="anonymous"
        />
        {showAnnotations && imageLoaded && (
          <canvas
            ref={canvasRef}
            className="absolute top-0 left-0 w-full h-full pointer-events-none"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain'
            }}
          />
        )}
      </div>
      
      <div className="absolute top-4 right-4 flex gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={handleZoomToggle}
          className="bg-card/90 backdrop-blur-sm border border-border hover:bg-muted"
          iconName={isZoomed ? 'ZoomOut' : 'ZoomIn'}
        />
      </div>

      {annotations && annotations?.length > 0 && (
        <div className="absolute top-4 left-4 bg-card/90 backdrop-blur-sm rounded-lg border border-border p-2">
          <div className="flex items-center gap-2 text-xs">
            <div className="w-2 h-2 rounded-full bg-primary" />
            <span className="font-medium">{annotations?.length} annotation{annotations?.length !== 1 ? 's' : ''}</span>
          </div>
        </div>
      )}

      {image?.caption && (
        <div className="p-3 bg-card border-t border-border">
          <p className="text-sm text-muted-foreground">{image?.caption}</p>
        </div>
      )}
    </div>
  );
};

export default AnnotatedImage;